import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationCircle, faChartLine, faPencilAlt, faTimes, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';

interface InstructorCourse {
  learnershipId: string;
  title: string;
}

interface AssignmentGrade {
  assignmentId: string;
  assignmentTitle: string;
  maxMark: number;
  grade: number | null;
  status: number;
}

interface GradebookEntry {
  enrollmentId: string;
  studentName: string;
  studentIdNumber: string;
  assignmentGrades: AssignmentGrade[];
  calculatedAveragePercent: number | null;
  overrideGrade: number | null;
  overrideReason: string | null;
  isPassing: boolean;
}

interface GradebookData {
  learnershipId: string;
  learnershipTitle: string;
  assignmentTitles: string[];
  entries: GradebookEntry[];
}

export default function InstructorGradebookPage() {
  const { accessToken } = useAuth();
  const [courses, setCourses] = useState<InstructorCourse[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [gradebook, setGradebook] = useState<GradebookData | null>(null);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [isLoadingGradebook, setIsLoadingGradebook] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [overrideEntry, setOverrideEntry] = useState<GradebookEntry | null>(null);
  const [overrideValue, setOverrideValue] = useState('');
  const [overrideReasonValue, setOverrideReasonValue] = useState('');
  const [isSavingOverride, setIsSavingOverride] = useState(false);
  const [overrideError, setOverrideError] = useState('');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setIsLoadingCourses(true);
        const response = await fetch('/api/instructor/courses', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) throw new Error('Failed to load courses');
        const data = await response.json();
        setCourses(data);
        if (data.length > 0) setSelectedCourseId(data[0].learnershipId);
      } catch (err) {
        console.error(err);
        setError('Failed to load your courses.');
      } finally {
        setIsLoadingCourses(false);
      }
    };
    fetchCourses();
  }, [accessToken]);

  useEffect(() => {
    if (!selectedCourseId) return;
    const fetchGradebook = async () => {
      try {
        setIsLoadingGradebook(true);
        setError(null);
        const response = await fetch(`/api/gradebook/course/${selectedCourseId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) throw new Error('Failed to load gradebook');
        setGradebook(await response.json());
      } catch (err) {
        console.error(err);
        setError('Failed to load the gradebook for this course.');
      } finally {
        setIsLoadingGradebook(false);
      }
    };
    fetchGradebook();
  }, [selectedCourseId, accessToken]);

  const openOverrideModal = (entry: GradebookEntry) => {
    setOverrideEntry(entry);
    setOverrideValue(entry.overrideGrade?.toString() ?? '');
    setOverrideReasonValue(entry.overrideReason ?? '');
    setOverrideError('');
  };

  const handleSaveOverride = async (clear = false) => {
    if (!overrideEntry) return;

    const value = clear ? null : overrideValue.trim() === '' ? null : parseInt(overrideValue, 10);
    if (!clear && value !== null && (isNaN(value) || value < 0 || value > 100)) {
      setOverrideError('Override grade must be between 0 and 100.');
      return;
    }

    try {
      setIsSavingOverride(true);
      const response = await fetch(`/api/gradebook/enrollment/${overrideEntry.enrollmentId}/override`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ overrideGrade: value, overrideReason: clear ? '' : overrideReasonValue }),
      });

      const data = await response.json();
      if (!response.ok) {
        setOverrideError(data.message || 'Failed to save override.');
        return;
      }

      setOverrideEntry(null);
      const refreshed = await fetch(`/api/gradebook/course/${selectedCourseId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (refreshed.ok) setGradebook(await refreshed.json());
    } catch (err) {
      console.error(err);
      setOverrideError('An error occurred while saving the override.');
    } finally {
      setIsSavingOverride(false);
    }
  };

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-brand-950">Gradebook</h1>
          <p className="text-sm text-brand-900/60 mt-1">Grades across all assignments for a course</p>
        </div>
        {!isLoadingCourses && courses.length > 0 && (
          <select
            value={selectedCourseId}
            onChange={(e) => setSelectedCourseId(e.target.value)}
            className="px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            {courses.map((c) => (
              <option key={c.learnershipId} value={c.learnershipId}>
                {c.title}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="p-8">
        {(isLoadingCourses || isLoadingGradebook) && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoadingCourses && courses.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faChartLine} className="h-12 w-12 text-brand-300 mb-4" />
            <p className="text-brand-900/60">You have no assigned courses yet.</p>
          </div>
        )}

        {!isLoadingGradebook && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoadingGradebook && !error && gradebook && (
          <div className="card-surface overflow-x-auto">
            {gradebook.entries.length === 0 ? (
              <div className="p-12 text-center text-brand-900/60">No students enrolled in this course.</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-canvas-50 text-xs font-bold uppercase tracking-wider text-brand-900/50">
                    <th className="text-left px-5 py-3">Student</th>
                    {gradebook.assignmentTitles.map((t) => (
                      <th key={t} className="text-left px-5 py-3 whitespace-nowrap">{t}</th>
                    ))}
                    <th className="text-left px-5 py-3">Average</th>
                    <th className="text-left px-5 py-3">Final Grade</th>
                    <th className="text-left px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-canvas-200">
                  {gradebook.entries.map((entry) => (
                    <tr key={entry.enrollmentId}>
                      <td className="px-5 py-4">
                        <p className="font-semibold text-brand-950">{entry.studentName}</p>
                        <p className="text-xs text-brand-900/50">{entry.studentIdNumber}</p>
                      </td>
                      {entry.assignmentGrades.map((g) => (
                        <td key={g.assignmentId} className="px-5 py-4 whitespace-nowrap">
                          {g.grade != null ? `${g.grade}/${g.maxMark}` : '—'}
                        </td>
                      ))}
                      <td className="px-5 py-4">
                        {entry.calculatedAveragePercent != null ? `${entry.calculatedAveragePercent}%` : '—'}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                            entry.isPassing ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {entry.overrideGrade != null ? `${entry.overrideGrade}% (override)` : entry.calculatedAveragePercent != null ? `${entry.calculatedAveragePercent}%` : 'No grades'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => openOverrideModal(entry)}
                          className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center gap-1.5"
                        >
                          <FontAwesomeIcon icon={faPencilAlt} className="h-3 w-3" />
                          Override
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {overrideEntry && (
        <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-brand-950">Override Final Grade</h3>
              <button onClick={() => setOverrideEntry(null)} className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60">
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <p className="text-sm text-brand-900/60 mb-4">
              {overrideEntry.studentName} · Calculated average:{' '}
              {overrideEntry.calculatedAveragePercent != null ? `${overrideEntry.calculatedAveragePercent}%` : 'No grades yet'}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Override Grade (%)</label>
                <input
                  type="number"
                  value={overrideValue}
                  onChange={(e) => setOverrideValue(e.target.value)}
                  placeholder="Leave blank to use calculated average"
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Reason</label>
                <textarea
                  value={overrideReasonValue}
                  onChange={(e) => setOverrideReasonValue(e.target.value)}
                  rows={2}
                  placeholder="Why is this grade being overridden?"
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              {overrideError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <FontAwesomeIcon icon={faExclamationCircle} className="h-4 w-4 text-red-700 flex-shrink-0" />
                  <p className="text-sm text-red-700">{overrideError}</p>
                </div>
              )}
            </div>

            <div className="flex justify-between gap-3 mt-6">
              <button
                onClick={() => handleSaveOverride(true)}
                disabled={isSavingOverride}
                className="px-4 py-2 text-brand-900/70 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm"
              >
                Clear override
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setOverrideEntry(null)}
                  className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSaveOverride(false)}
                  disabled={isSavingOverride}
                  className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={isSavingOverride ? faSpinner : faCheckCircle} className={`h-3.5 w-3.5 ${isSavingOverride ? 'animate-spin' : ''}`} />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
